import type { AttributionRequest } from '@kcm/contracts'
import { endpoints } from './endpoints.js'

/**
 * Partner attribution capture (PLAN.md §19.1).
 *
 * A landing looks like `https://my.kcmtra.de/?lid=24795&pid=172726`. Those two
 * numbers decide which partner gets paid for the signup, so they are posted to
 * the server on first paint and then live server-side under an opaque cookie —
 * never carried through the funnel in a form field the user could edit.
 *
 * Posted for every landing, parameters or not: a bare visit is still a landing,
 * and the funnel-completion rate in §1.1 is meaningless without the denominator.
 */

/** Sent once per tab. A remount must not double-count a landing. */
const SENT_KEY = 'kcm.attribution.sent'

const UTM_PREFIX = 'utm_'
/** Enough for the five standard tags plus a couple of house ones. */
const MAX_UTM_KEYS = 8

export function readAttribution(url: string, referrer: string): AttributionRequest {
  const params = new URL(url).searchParams
  const utm: Record<string, string> = {}
  for (const [key, value] of params) {
    if (!key.startsWith(UTM_PREFIX) || !value) continue
    if (Object.keys(utm).length >= MAX_UTM_KEYS) break
    // The contract caps these too. Trimming here as well keeps an absurd URL
    // from being rejected wholesale when the useful parameters are fine.
    utm[key.slice(0, 64)] = value.slice(0, 256)
  }

  return {
    ...numeric('lid', params.get('lid')),
    ...numeric('pid', params.get('pid')),
    ...(Object.keys(utm).length > 0 ? { utm } : {}),
    landingUrl: url.slice(0, 2048),
    ...(referrer ? { referrer: referrer.slice(0, 2048) } : {}),
  }
}

/**
 * Omits the key entirely when the value is not a positive integer.
 *
 * Sending `lid=abc` would fail contract validation and take the whole landing
 * record with it, losing the utm tags and the visit itself over one bad
 * parameter that was probably never ours.
 */
function numeric(key: 'lid' | 'pid', raw: string | null) {
  if (!raw) return {}
  const n = Number(raw)
  if (!Number.isInteger(n) || n <= 0 || n > 2_147_483_647) return {}
  return { [key]: n }
}

/**
 * Fire-and-forget. Attribution must never block or break the funnel: a partner
 * who loses a credit is a billing conversation, a user who cannot sign up
 * because a tracking call failed is lost revenue.
 */
export async function captureAttribution(storage: Storage = sessionStorage): Promise<void> {
  try {
    if (storage.getItem(SENT_KEY)) return
    // Marked before the request, not after. A double-mount in StrictMode fires
    // twice within milliseconds, well before any response could come back.
    storage.setItem(SENT_KEY, '1')
  } catch {
    // Private browsing can throw on storage access. Losing the guard is fine;
    // the server dedupes on its own cookie.
  }

  try {
    await endpoints.attribution(readAttribution(window.location.href, document.referrer))
  } catch {
    /* never surfaced */
  }
}
